import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  Briefcase,
  Plus,
  Search,
  Users,
} from "lucide-react";
import { useMemo, useState } from "react";
import Swal from "sweetalert2";
import authService from "../../api/authService";
import jobPostingApi from "../../api/jobPosting";
import subscriptionApi from "../../api/subscription";
import Button from "../../components/common/Button";
import ErrorState from "../../components/common/ErrorState";
import Spinner from "../../components/common/Spinner";
import SubscriptionStatus from "../../components/common/SubscriptionStatus";
import EmptyJobState from "../../components/recruiter/EmptyJobState";
import { StatCard } from "../../components/recruiter/StatCard";
import { useAuthStore } from "../../store/authStore.js";
import JobPostingCard from "./components/JobPostingCard";
import useSubscription from "../../hooks/useSubscription.js";

const RecruiterDashboard = ({ onNavigate }) => {
  const user = useAuthStore(s => s.user);

  const [activeTab, setActiveTab] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const JOBS_PER_PAGE = 10;

  const [selectedJobs, setSelectedJobs] = useState([]);
  // const [subscriptionData, setSubscriptionData] = useState(null);

  const {
    data: jobsResponse,
    isLoading,
    isError,
    error: jobsError,
    refetch: refetchJobs,
  } = useQuery({
    queryKey: ["my-jobs", currentPage, activeTab, searchTerm],
    queryFn: () =>
      jobPostingApi.getMyJobPostings({
        page: currentPage,
        status: activeTab !== "all" ? activeTab : undefined,
        search: searchTerm || undefined,
      }),
    placeholderData: (previousData) => previousData,
  });

  const { data: stats } = useQuery({
    queryKey: ["application-stats"],
    queryFn: async () => {
      const res = await authService.getApplicationStats();
      return res.data;
    },
  });
  const { data: subscriptionData} = useSubscription()

  // const { data: subscriptionData } = useQuery({
  //   queryKey: ["my-subscription"],
  //   queryFn: async () => {
  //     const res = await subscriptionApi.getMySubscription();
  //     return res.data;
  //   },
  //   staleTime: 5 * 60 * 1000,
  // });

  const jobs = jobsResponse?.data?.results ?? [];
  const totalJobs = stats?.total_jobs ?? 0;
  const activeJobs = stats?.active_jobs ?? 0;
  const draftJobs = totalJobs - activeJobs;
  const totalPages = Math.ceil(totalJobs / JOBS_PER_PAGE);
  const isDashboardLoading = isLoading || !stats;

  const dashboardStats = useMemo(() => {
    if (isDashboardLoading) {
      return {
        totalJobs: 0,
        totalApplications: 0,
        newApplications: 0,
        avgConversion: "0.00",
        activeJobs: 0,
      };
    }
    return {
      totalJobs: totalJobs,
      totalApplications: stats.total_applications || 0,
      newApplications: stats.new_applications || 0,
      avgConversion: "N/A",
      activeJobs: stats.active_jobs || 0,
    };
  }, [jobs, stats]);

  const filteredAndSortedJobs = useMemo(() => {
    let filtered = jobs;
    if (activeTab !== "all") {
      filtered = filtered.filter((job) => job.status === activeTab);
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (job) =>
          job.title?.toLowerCase().includes(term) ||
          job.department?.toLowerCase().includes(term) ||
          job.location?.toLowerCase().includes(term),
      );
    }
    return [...filtered].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at),
    );
  }, [jobs, activeTab, searchTerm]);

  const paginatedJobs = useMemo(() => {
    const startIndex = (currentPage - 1) * JOBS_PER_PAGE;
    return filteredAndSortedJobs.slice(startIndex, startIndex + JOBS_PER_PAGE);
  }, [filteredAndSortedJobs, currentPage]);

  // const totalPages = Math.ceil(filteredAndSortedJobs.length / JOBS_PER_PAGE);

  const handleJobUpdate = (updatedJob) => {
    setJobs((prevJobs) =>
      prevJobs.map((job) =>
        job.id === updatedJob.id ? { ...job, ...updatedJob } : job,
      ),
    );
  };

  const handleSelectJob = (jobId) => {
    setSelectedJobs((prev) =>
      prev.includes(jobId)
        ? prev.filter((id) => id !== jobId)
        : [...prev, jobId],
    );
  };

  const getWelcomeMessage = () => {
    if (!user) return "Welcome!";
    return `Welcome back, ${
      user.first_name || user.company_name || "Recruiter"
    }!`;
  };

  const recruiterProfilestatus = user?.company_verification_status; // 'verified', 'unverified', null (pending)
  const getVerificationBadge = () => {
    if (recruiterProfilestatus === "verified") {
      return (
        <div className="ml-3 flex items-center gap-1.5 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium">
          <span className="w-2 h-2 bg-green-500 rounded-full"></span>
          Verified ✓
        </div>
      );
    }

    if (recruiterProfilestatus === "unverified") {
      return (
        <div className="ml-3 flex items-center gap-1.5 bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-medium">
          <span className="w-2 h-2 bg-red-500 rounded-full"></span>
          Rejected
        </div>
      );
    }

    // null or any other status = pending
    return (
      <div className="ml-3 flex items-center gap-1.5 bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-sm font-medium">
        <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
        Pending Verification
      </div>
    );
  };

  if (isLoading) {
    return <Spinner />;
  }

  if (isError) {
    return (
      <ErrorState
        error={jobsError?.message || "Failed to load jobs"}
        onRetry={refetchJobs}
      />
    );
  }

  const tabCounts = {
    all: totalJobs,
    active: activeJobs,
    draft: draftJobs,
    // closed: jobs.filter(j => j.status === 'closed').length,
  };

  const hasActiveSubscription = subscriptionData?.has_subscription;
  const hasVerifiedRecruiterProfile = recruiterProfilestatus === "verified";

  const handlePostNewJob = () => {
    if (hasActiveSubscription && hasVerifiedRecruiterProfile) {
      onNavigate("/post-job");
    } else if (!hasVerifiedRecruiterProfile) {
      Swal.fire({
        title: "Company Profile Not Verified",
        text: "Your company profile needs to be verified before you can post jobs. Please complete your profile and submit it for verification.",
        icon: "warning",
        confirmButtonText: "Go to Profile",
        cancelButtonText: "Not now",
        showCancelButton: true,
      }).then((result) => {
        if (result.isConfirmed) {
          onNavigate("/company-profile");
        } else {
      navigate(-1);
    }
      })
    }
    else {
      Swal.fire({
        title: "Ready to Post a New Job?",
        text: "Click confirm to start creating a new job posting. An active subscription and a verified company profile are required to post jobs.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, let's go!",
        cancelButtonText: "Not now",
      }).then((result) => {
        if (result.isConfirmed) {
          onNavigate("/pricing");
        } else {
      navigate(-1);
    }
      });
    }
  };
  return (
    <div className="bg-secondary-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <div className="flex items-center mb-2">
              <div className="w-10 h-10 bg-primary-600 rounded-xl flex items-center justify-center mr-3">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-4xl font-bold text-secondary-900">
                {getWelcomeMessage()}
              </h1>
              {getVerificationBadge()}
            </div>
            <p className="mt-2 text-secondary-600 text-lg">
              Here's a comprehensive overview of your job postings and
              recruitment metrics.
            </p>
          </div>
          <div className="mt-6 md:mt-0 flex flex-col sm:items-end gap-4">
            {subscriptionData && (
              <SubscriptionStatus subscriptionData={subscriptionData} />
            )}
            <Button
              variant="primary"
              onClick={handlePostNewJob}
              icon={<Plus size={18} />}
              className="shadow-lg hover:shadow-xl transition-shadow duration-300"
            >
              Post New Job
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          <StatCard
            title="Total Jobs"
            value={dashboardStats.totalJobs}
            change={`${dashboardStats.activeJobs} active`}
            icon={<Briefcase className="w-7 h-7" />}
            bgColor="bg-blue-50"
            iconColor="bg-blue-600"
            textColor="text-blue-800"
            changeColor="text-blue-600"
            onClick={() => onNavigate("/manage-jobs")}
          />
          <StatCard
            title="Total Applications"
            value={dashboardStats.totalApplications}
            change={`+${dashboardStats.newApplications} new this week`}
            icon={<Users className="w-7 h-7" />}
            bgColor="bg-green-50"
            iconColor="bg-green-600"
            textColor="text-green-800"
            changeColor="text-green-600"
            onClick={() => onNavigate("/view-applications/all")}
          />
          {/* <StatCard 
            title="Total Views" 
            value={"N/A"} 
            change={`Feature coming soon`}
            icon={<Eye className="w-7 h-7" />}
            bgColor="bg-purple-50"
            iconColor="bg-purple-600"
            textColor="text-purple-800"
            changeColor="text-purple-600"
          />
          <StatCard 
            title="Avg. Conversion" 
            value={`${dashboardStats.avgConversion}`} 
            change={`Feature coming soon`}
            icon={<TrendingUp className="w-7 h-7" />}
            bgColor="bg-indigo-50"
            iconColor="bg-indigo-600"
            textColor="text-indigo-800"
            changeColor="text-indigo-600"
          /> */}
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 mb-8">
          <div className="flex flex-col lg:flex-row gap-6 items-start lg:items-center">
            <div className="relative flex-1 max-w-md">
              <Search
                className="absolute left-4 top-1/2 -translate-y-1/2 text-secondary-400"
                size={20}
              />
              <input
                type="text"
                placeholder="Search jobs by title, department, or location..."
                className="w-full pl-12 pr-4 py-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300 text-lg"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-100">
            <div className="flex items-center gap-3 flex-wrap">
              {Object.entries(tabCounts).map(([tab, count]) => (
                <button
                  key={tab}
                  className={`px-6 py-3 text-sm font-semibold rounded-xl transition-all duration-300 ${
                    activeTab === tab
                      ? "bg-primary-100 text-primary-700 shadow-md border-2 border-primary-200"
                      : "bg-gray-50 text-gray-600 hover:bg-gray-100 border-2 border-transparent"
                  }`}
                  onClick={() => {
                    setActiveTab(tab);
                    setCurrentPage(1);
                  }}
                >
                  {tab === "all"
                    ? "All Jobs"
                    : tab.charAt(0).toUpperCase() + tab.slice(1)}
                  <span className="ml-2 bg-white px-2 py-1 rounded-full text-xs font-bold">
                    {count}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="max-h-[calc(100vh-200px)] overflow-y-auto pr-2 -mr-2">
            <div className="space-y-6">
              {jobs.length === 0 ? (
                <EmptyJobState />
              ) : (
                jobs.map((job) => (
                  <JobPostingCard
                    key={job.id}
                    job={job}
                    onNavigate={onNavigate}
                    onUpdate={handleJobUpdate}
                    onSelect={handleSelectJob}
                    isSelected={selectedJobs.includes(job.id)}
                  />
                ))
              )}
            </div>
          </div>

          {totalPages > 1 && (
            <div className="mt-8 flex justify-between items-center bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <Button
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={!jobsResponse?.data?.previous}
                variant="secondary"
                icon={<ArrowLeft size={16} />}
                className="disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </Button>
              <div className="flex items-center gap-2">
                <span className="text-secondary-600">Page</span>
                <span className="bg-primary-600 text-white px-4 py-2 rounded-xl font-bold text-lg">
                  {currentPage}
                </span>
                <span className="text-secondary-600">of {totalPages}</span>
              </div>
              <Button
                onClick={() => setCurrentPage((p) => p + 1)}
                disabled={!jobsResponse?.data?.next}
                variant="secondary"
                icon={<ArrowRight size={16} />}
                iconPosition="right"
                className="disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecruiterDashboard;
